// `RouterProvider` is re-exported from `react-router/dom` by `@/js/libs/router` — that build wires up
// `react-dom`'s `flushSync`, which the top-level entry (meant for non-DOM environments) leaves unset.
import { RouterProvider, createRouter, Navigate, type DataRouter } from '@/js/libs/router';
import { not, or, PrivateRoute, Roles } from '@/js/libs/use-auth';

import 'src/api.ts';
import { Layout } from './components/Layout/index.tsx';
import RoleBasedRedirector, { type Condition } from './components/RoleBasedRedirector.tsx';
import Providers from './Providers.tsx';
import { clickPreviewRouteObject } from './routes/clickPreview.tsx';
import {
  accountRouteObject,
  adminRouteObject,
  adTagsRouteObject,
  analogOperationsRouteObject,
  analyticsToolObject,
  placementConfigsRouteObject,
  businessIntelligenceRouteObject,
  complianceRouteObject,
  creativesRouteObject,
  crmRouteObject,
  dashboardRouteObject,
  digitalOperationsRouteObject,
  financesRouteObject,
  genAIObject,
  headerTagRouteObject,
  notificationsRouteObject,
  orders1RouteObject,
  orders2RouteObject,
  ordersPostCampaignsRouteObject,
  printOperationsRouteObject,
  programmaticDealsRouteObject,
  publishersRouteObject,
  salesRouteObject,
  selfBookingRouteObject,
  semantiqRouteObject,
  productCatalogueRouteObject,
  companySettingsRouteObject,
  programmaticMappingRouteObject,
  settingsRouteObject,
  supportCenterRouteObject,
  supportChatRouteObject,
  yaleoRouteObject,
} from './routes/index.ts';
import { postCrmRouteObject } from './routes/postCrm.tsx';
import Authentication from './scenes/Authentication/index.ts';
import Campaign from './scenes/Campaign/index.ts';
import Mail, { PromotionMailSearchParams } from './scenes/Mail/index.ts';
import Status from './scenes/Status/index.ts';

const homeConditions: Condition[] = [
  { roles: Roles.YALEO_SELF_BOOKING_USER, to: '/self-booking/campaigns' },
  { roles: Roles.AGENCY, to: '/orders' },
  { roles: Roles.YALEO_SELF_BOOKING, to: '/self-booking/campaigns' },
  { roles: Roles.POSTADMANAGER, to: '/orders/post-campaigns' },
  { roles: Roles.PUBLISHER, to: '/publishers' },
  { roles: or(Roles.SALESFUNNEL, Roles.ADMIN), to: '/sales' },
  { roles: Roles.SEMANTIQ, to: '/semantiq' },
];

const index = true;

export const ROUTER = createRouter([
  {
    path: '/',
    element: <Providers />,
    children: [
      {
        element: <PrivateRoute roles={Roles.ANONYMOUS} redirectPath='/' />,
        children: [
          { path: 'login', element: <Navigate to='/authentication' /> },
          {
            path: 'authentication',
            children: [
              { index, Component: Authentication.Components.Redirector },
              {
                path: 'sso',
                children: [
                  { index, Component: Authentication.Components.Redirector },
                  { path: ':issuerName', element: <Authentication.Components.SSO /> },
                ],
              },
              { path: 'autologin', Component: Authentication.Components.Autologin },
              {
                Component: Authentication.Components.Layout,
                children: [
                  { path: 'login', Component: Authentication.Forms.Login },
                  { path: 'create-account', Component: Authentication.Forms.Registration },
                  { path: 'verification', Component: Authentication.Forms.Verification },
                  { path: 'restore-password', Component: Authentication.Forms.RestorePassword },
                  { path: 'change-password/:hash', Component: Authentication.Forms.ChangePassword },
                  { path: 'activate/:hash', Component: Authentication.Forms.ActivateUser },
                ],
              },
            ],
          },
        ],
      },
      {
        element: <PrivateRoute roles={not(Roles.ANONYMOUS)} redirectPath='/login' />,
        children: [clickPreviewRouteObject],
      },
      {
        element: <Layout />,
        children: [
          accountRouteObject,
          {
            element: <PrivateRoute roles={not(Roles.ANONYMOUS)} redirectPath='/login' />,
            children: [
              {
                index,
                element: <RoleBasedRedirector conditions={homeConditions} fallbackPath='/account/edit-settings' />,
              },
              {
                path: 'mail',
                Component: Mail.Helmet,
                children: [
                  {
                    element: <PrivateRoute roles={Roles.SALESFUNNEL} noAccessComponent={Status.NotFound} />,
                    children: [
                      { path: 'promotion/:promotionId', Component: Mail.PromotionMail, searchParams: PromotionMailSearchParams.parse },
                    ],
                  },
                ],
              },
              notificationsRouteObject,
              {
                element: (
                  <PrivateRoute
                    roles={or(
                      Roles.AGENCY,
                      Roles.ADMIN,
                      Roles.POSTADMANAGER,
                      Roles.MANAGE_DIGITAL_PRODUCTS,
                      Roles.MANAGE_RADIO_PRODUCTS,
                      Roles.MANAGE_TV_PRODUCTS,
                      Roles.MANAGE_PRINT_PRODUCTS,
                      Roles.MANAGE_POS_PRODUCTS,
                    )}
                    noAccessComponent={Status.NotFound}
                  />
                ),
                children: [
                  {
                    Component: Campaign.FiltersProvider,
                    children: [orders1RouteObject, crmRouteObject],
                  },
                ],
              },
              postCrmRouteObject,
              ordersPostCampaignsRouteObject,
              dashboardRouteObject,
              orders2RouteObject,
              programmaticDealsRouteObject,
              creativesRouteObject,
              businessIntelligenceRouteObject,
              semantiqRouteObject,
              publishersRouteObject,
              adTagsRouteObject,
              headerTagRouteObject,
              yaleoRouteObject,
              selfBookingRouteObject,
              digitalOperationsRouteObject,
              analogOperationsRouteObject,
              financesRouteObject,
              productCatalogueRouteObject,
              companySettingsRouteObject,
              programmaticMappingRouteObject,
              settingsRouteObject,
              salesRouteObject,
              adminRouteObject,
              complianceRouteObject,
              genAIObject,
              placementConfigsRouteObject,
              analyticsToolObject,
              printOperationsRouteObject,
              supportCenterRouteObject,
              supportChatRouteObject,
              { path: '*', Component: Status.NotFound },
            ],
          },
          { path: '403', Component: Status.Forbidden },
          { path: '404', Component: Status.NotFound },
        ],
      },
      { path: 'maintenance', Component: Status.Maintenance },
    ],
  },
]) satisfies DataRouter;

// `useTransitions={false}` keeps the v6 behaviour react-router v7 dropped: router state updates stay
// plain `setState` calls instead of being wrapped in `React.startTransition`. Under a transition React
// keeps the previous page on screen instead of showing the `Suspense` fallback, which would silently
// remove the loader from every `React.lazy` scene transition in this app.
export const AppRouter = () => <RouterProvider router={ROUTER} useTransitions={false} />;
